import { useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';

import { useUpdateVenuaPlannerSerie } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  buildSerieDetailFormSchema,
  fromGetSerieDetailResponse,
  type SerieDetailFormValues,
} from './serieDetailFormSchema';
import { toUpdateSerieRequestBody } from './toSerieDetailRequest';
import type { GetSerieDetailResponse } from '@/api/generated/schemas';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  TransportType: 'transportType',
  CapacityPerTerm: 'capacityPerTerm',
  MainProgramLengthDays: 'mainProgramLengthDays',
  Note: 'note',
};

interface UseSerieDetailFormOptions {
  productId: string;
  serieId: string;
  /** Detail seriálu pro výchozí hodnoty (form dostane správné defaulty hned, žádný `form.reset`). */
  detail: GetSerieDetailResponse;
  /** Voláno po úspěšném uložení (invalidace + toast). NEzavírá okno – o zavření rozhoduje volající. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na konkrétní pole (např. síťová chyba). */
  onError?: (error: unknown) => void;
}

/**
 * Zapouzdřuje formulářovou logiku editace termínového seriálu – TanStack Form se Zod validací, `UpdateSerie`
 * mutace a promítnutí serverových validačních chyb do polí (stejný vzor jako editace projektu).
 *
 * Vrací navíc `submit()` vracející `Promise<boolean>` (úspěch uložení) pro „Uložit & zpět“, kde se okno
 * zavře jen po úspěšném uložení.
 */
export function useSerieDetailForm({
  productId,
  serieId,
  detail,
  onSaved,
  onError,
}: UseSerieDetailFormOptions) {
  const { t } = useTranslation('venua-planner');
  const schema = useMemo(() => buildSerieDetailFormSchema(t), [t]);
  const updateMutation = useUpdateVenuaPlannerSerie();
  const lastSubmitOkRef = useRef(false);

  const form = useForm({
    defaultValues: fromGetSerieDetailResponse(detail),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await updateMutation.mutateAsync({
          productId,
          serieId,
          data: toUpdateSerieRequestBody(value),
        });
        lastSubmitOkRef.current = true;
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + submit a vrátí, zda uložení proběhlo úspěšně. */
  const submit = async (): Promise<boolean> => {
    lastSubmitOkRef.current = false;
    await form.handleSubmit();
    return lastSubmitOkRef.current;
  };

  return { form, submit, isSubmitting: updateMutation.isPending };
}

/** Typ TanStack Form instance editace seriálu (sdílený do `SerieBasicsForm`). */
export type SerieDetailFormApi = ReturnType<typeof useSerieDetailForm>['form'];

export type { SerieDetailFormValues };
